import React, { useCallback, useState } from "react";
import PropTypes from "prop-types";

import { UnifiedUserUMS } from "../../../types/Users";
import { deleteUser, purgeUsers, searchUnifiedUsers } from "../../../utils/api/Users";
import UnifiedUsersNew from "./UnifiedUsersNew";
import { useToast } from "@familyzone/component-library";
import { PurgeType } from "../../../utils/api/Helpers";
import { IdentitySearch, IdentitySearchParams } from "../IdentitySearch/IdentitySearch";

const UnifiedUsers: React.FC = (): JSX.Element => {
  const PAGE_SIZE = 50;
  const { successToast, errorToast } = useToast();
  const [users, setUsers] = useState<UnifiedUserUMS[]>([]);
  const [loading, setLoading] = useState(false);

  // Read query parameter from URL synchronously during initialization
  const getInitialSearch = (): string | undefined => {
    const queryString = window.location.search || window.location.hash.split("?")[1] || "";
    const urlParams = new URLSearchParams(queryString);
    return urlParams.get("username") || undefined;
  };

  const [initialSearch] = useState<string | undefined>(getInitialSearch);

  const handlePurgeUsers = (type: PurgeType) => {
    purgeUsers(type)
      .then(() => {
        successToast({
          title: "Success!",
          description: "We've received the request and it has been sent for processing.",
        });
      })
      .catch(() => {
        errorToast({
          title: "Error",
          description: "We're not able to process the purge at this time. Please try again later.",
        });
      });
  };

  const search = useCallback((params: IdentitySearchParams): Promise<UnifiedUserUMS[]> => {
    return searchUnifiedUsers(params.query, params.pageSize);
  }, []);

  const handleDelete = (id: string) => {
    deleteUser(id)
      .then(() => {
        successToast({
          title: "Success!",
          description: "We've received the request and it has been sent for processing.",
        });
      })
      .catch(() => {
        errorToast({
          title: "Error",
          description: "We're not able to delete the user at this time. Please try again later.",
        });
      });
  };

  return (
    <UnifiedUsersNew
      users={users}
      loaded={!loading}
      onPurgeUsers={handlePurgeUsers}
      onDeleteUser={handleDelete}
      withinTableChildren={
        <IdentitySearch<UnifiedUserUMS>
          setState={setUsers}
          setLoading={setLoading}
          initialSearch={initialSearch}
          searchFn={search}
          pageSize={PAGE_SIZE}
        />
      }
      isSearching={loading}
    />
  );
};

UnifiedUsers.contextTypes = {
  router: PropTypes.object.isRequired,
};

export default UnifiedUsers;
